(() => {
  const roots = document.querySelectorAll(
    '[data-mm-directory-cycle]:not([data-mm-directory-enhanced])'
  );
  if (!roots.length) return;

  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const coarsePointerQuery = window.matchMedia('(hover: none), (pointer: coarse)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const loadHoverImage = (image) => {
    if (!image) return Promise.resolve(false);

    const hoverSource = image.dataset.hoverSrc;
    if (!hoverSource) {
      return Promise.resolve(image.complete && image.naturalWidth > 1);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve(image.naturalWidth > 1);
      };

      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });

      image.loading = 'eager';
      if (image.dataset.hoverSrcset) image.srcset = image.dataset.hoverSrcset;
      image.src = hoverSource;
      image.removeAttribute('data-hover-src');
      image.removeAttribute('data-hover-srcset');

      if (image.complete) queueMicrotask(finish);
    });
  };

  const setupFinePointerLoading = (root) => {
    const hoverImages = Array.from(root.querySelectorAll('[data-hover-src]'));
    if (!hoverImages.length) return;

    if (!('IntersectionObserver' in window)) {
      hoverImages.forEach((image) => loadHoverImage(image));
      return;
    }

    const imageObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadHoverImage(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '320px 0px' }
    );

    hoverImages.forEach((image) => imageObserver.observe(image));
  };

  const setupMobileSequence = (root) => {
    const cards = Array.from(root.querySelectorAll('[data-mm-cycle-card]')).filter((card) => {
      return Boolean(card.querySelector('[data-hover-src]'));
    });
    if (!cards.length) return;

    let activeIndex = -1;
    let sequenceTimer = null;
    let sequenceToken = 0;
    let isGridVisible = false;
    let isRunning = false;

    const hoverImageAt = (index) => cards[index]?.querySelector('[data-hover-src], [data-mm-hover-loaded]');

    const primeCard = (index) => {
      if (index < 0 || index >= cards.length) return Promise.resolve(false);
      const image = hoverImageAt(index);
      if (!image) return Promise.resolve(false);
      image.dataset.mmHoverLoaded = 'true';
      return loadHoverImage(image);
    };

    const stopSequence = () => {
      isRunning = false;
      sequenceToken += 1;
      window.clearTimeout(sequenceTimer);
      sequenceTimer = null;
      cards.forEach((card) => card.classList.remove('is-auto-revealed'));
    };

    const shouldRun = () => {
      return coarsePointerQuery.matches
        && !finePointerQuery.matches
        && !reducedMotionQuery.matches
        && document.visibilityState !== 'hidden'
        && isGridVisible;
    };

    const showNextCard = async () => {
      if (!isRunning || !shouldRun()) {
        stopSequence();
        return;
      }

      const nextIndex = (activeIndex + 1) % cards.length;
      const currentToken = ++sequenceToken;
      const imageReady = await primeCard(nextIndex);

      if (!imageReady || !isRunning || currentToken !== sequenceToken || !shouldRun()) {
        if (isRunning && shouldRun()) {
          activeIndex = nextIndex;
          sequenceTimer = window.setTimeout(showNextCard, 300);
        }
        return;
      }

      cards.forEach((card, index) => {
        card.classList.toggle('is-auto-revealed', index === nextIndex);
      });
      activeIndex = nextIndex;

      const followingIndex = (nextIndex + 1) % cards.length;
      primeCard(followingIndex);
      sequenceTimer = window.setTimeout(showNextCard, 2400);
    };

    const startSequence = () => {
      if (isRunning || !shouldRun()) return;
      isRunning = true;
      showNextCard();
    };

    const syncSequence = () => {
      if (shouldRun()) {
        startSequence();
      } else {
        stopSequence();
      }
    };

    if ('IntersectionObserver' in window) {
      const gridObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isGridVisible = entry.isIntersecting;
            syncSequence();
          });
        },
        { threshold: 0.06 }
      );
      gridObserver.observe(root);
    } else {
      isGridVisible = true;
    }

    document.addEventListener('visibilitychange', syncSequence);

    [finePointerQuery, coarsePointerQuery, reducedMotionQuery].forEach((query) => {
      if (typeof query.addEventListener === 'function') {
        query.addEventListener('change', syncSequence);
      } else if (typeof query.addListener === 'function') {
        query.addListener(syncSequence);
      }
    });

    syncSequence();
  };

  roots.forEach((root) => {
    root.dataset.mmDirectoryEnhanced = 'true';

    if (finePointerQuery.matches) {
      setupFinePointerLoading(root);
    } else if (coarsePointerQuery.matches && !reducedMotionQuery.matches) {
      setupMobileSequence(root);
    }
  });

  const houseEdit = document.querySelector('[data-mm-house-edit]');
  if (houseEdit && !houseEdit.dataset.mmMeasured) {
    houseEdit.dataset.mmMeasured = 'true';

    const publishHouseEditEvent = (eventName, detail) => {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(eventName, detail);
      }
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    };

    const viewDetail = {
      surface: 'shop_by_collection',
      edit_count: houseEdit.querySelectorAll('[data-mm-house-edit-link]').length,
      path: window.location.pathname,
    };

    if ('IntersectionObserver' in window) {
      const houseEditObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
          publishHouseEditEvent('mm:house_edit_view', viewDetail);
          observer.disconnect();
        },
        { threshold: [0.5] }
      );
      houseEditObserver.observe(houseEdit);
    }

    houseEdit.addEventListener('click', (event) => {
      const link = event.target.closest('[data-mm-house-edit-link]');
      if (!link) return;
      publishHouseEditEvent('mm:house_edit_click', {
        surface: 'shop_by_collection',
        collection_handle: link.dataset.mmHouseEditHandle || '',
        position: Number(link.dataset.mmHouseEditPosition || 0),
        path: window.location.pathname,
      });
    });
  }
})();
